import * as React from "react";
import { homeLink, linksOf, request } from "../../../services/cpln";
import { Kind, List } from "../../../schema/types/base";
import { Query, QuerySort } from "../../../schema/types/query/query";

export interface UseTableItemQueryDataOptions {
  fetchOnInit?: boolean;
  query?: Query;
}

const defaultOptions: UseTableItemQueryDataOptions = {
  fetchOnInit: true,
  query: undefined,
};

export function useTableItemQueryData<T>(kind: Kind, _opts: UseTableItemQueryDataOptions = {}) {
  const opts = React.useMemo(() => ({ ...defaultOptions, ..._opts }), [kind, JSON.stringify(_opts)]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [items, setItems] = React.useState<T[]>([]);
  const [nextLink, setNextLink] = React.useState<string | null>(null);

  // Sort state (from table header clicks)
  const defaultSort: QuerySort = { by: "name", order: "asc" };
  const [sort, setSort] = React.useState<QuerySort>(defaultSort);
  // Search state (from global search input)
  const [search, setSearch] = React.useState("");
  // CPLN Query builder state
  const [cplnQuery, setCplnQuery] = React.useState<Query | null>(null);

  const hasMore = !!nextLink;

  // Compose the effective query from all sources
  const effectiveQuery = React.useMemo<Query>(() => {
    // When CPLN Query builder is active, it takes full control
    if (cplnQuery) {
      return cplnQuery;
    }

    // Build from base query + sort + search
    const terms = [...(opts.query?.spec?.terms || [])];
    if (search) {
      terms.push({ property: "name", op: "~", value: search });
    }

    const effectiveSort = sort;

    return {
      kind: kind as any,
      spec: {
        match: opts.query?.spec?.match || "all",
        terms: terms,
        sort: effectiveSort,
      },
    };
  }, [cplnQuery, opts.query, sort, search, kind]);

  async function fetchItems(automated: boolean = false) {
    try {
      if (!automated) {
        setIsLoading(true);
        setItems([]);
        setNextLink(null);
      }

      const url = homeLink(kind) + "/-query";

      const { data } = await request<List<T>>({ method: "post", url, body: effectiveQuery });
      setItems(data.items);
      setNextLink(linksOf(data).next || null);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      console.error(`Failed to fetch items of ${kind}`, errorMessage);
    }
  }

  async function fetchNextPage() {
    if (!nextLink || isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const { data } = await request<List<T>>({ url: nextLink });
      setItems((prev) => [...prev, ...data.items]);
      setNextLink(linksOf(data).next || null);
      setIsLoadingMore(false);
    } catch (e) {
      setIsLoadingMore(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      console.error(`Failed to fetch next page of ${kind}`, errorMessage);
    }
  }

  // Fetch on init
  const [initialFetchDone, setInitialFetchDone] = React.useState(false);
  React.useEffect(() => {
    if (opts.fetchOnInit) {
      fetchItems();
    }
    setInitialFetchDone(true);
  }, []);

  // Refetch when effective query changes (after initial fetch)
  React.useEffect(() => {
    if (!initialFetchDone) return;
    fetchItems();
  }, [effectiveQuery]);

  async function refetch(automated: boolean = false) {
    await fetchItems(automated);
  }

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    fetchItems,
    fetchNextPage,
    refetch,
    sort,
    setSort,
    search,
    setSearch,
    cplnQuery,
    setCplnQuery,
    // Keep visibleItems as alias for backwards compat during migration
    visibleItems: items,
    // Keep these for backwards compat with CPLN Query builder
    query: cplnQuery,
    setQuery: setCplnQuery,
    isLoadingQueried: isLoading,
    fetchQueriedItems: fetchItems,
  };
}
